import api from './api';
import { SESSION_ID } from './trackAction';

// Passive click collector — the raw-gesture layer BELOW ui_actions.
//
// ui_actions is curated semantics: someone wrote a track() call, so the click
// exists. This layer answers the question no instrumentation can anticipate:
// "where do people click and NOTHING happens?" A spot several different people
// tap in vain is almost always a feature the screen should have, or a UI that
// promises something it doesn't do. It also gives every click an x/y, which is
// what makes a visual heatmap possible at all.
//
// Privacy: not one character of screen content leaves the page. Only the tag +
// up to 3 stable CSS classes of the element, and the route with ids templated
// out (/tickets/8652 → /tickets/:id). In a helpdesk the ticket subject sits in
// the exact row being clicked — same care as ui_actions' target rule.
//
// Design constraints (shared with trackAction.js):
//  - A click must never wait on the network → buffered, flushed in batches.
//  - Telemetry must never break the app → every failure path is swallowed.
//  - Listener is {capture: true, passive: true}: a click stopped by some
//    handler's stopPropagation is still a click the person made, and we must
//    never add latency to the real handler.

const FLUSH_MS = 15000;
const MAX_BUFFER = 25; // matches MAX_PER_FLUSH in routes/uiClicks.js

let buffer = [];
let timer = null;
let installed = false;

function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (buffer.length === 0) return;
  const payload = { clicks: buffer.slice(0, MAX_BUFFER) };
  buffer = [];
  // No token → nothing to send: the POST would 401 and, worse, drag the user to
  // the login screen. `_telemetry` tells the interceptor this request never
  // touches the session. (Deliberately NOT sendBeacon: beacons can't carry the
  // Authorization header — lesson already paid in trackAction.js.)
  try {
    if (!localStorage.getItem('accessToken')) return;
  } catch { return; }
  api.post('/ui-clicks', payload, { _telemetry: true }).catch(() => {});
}

// ── Route templating ────────────────────────────────────────────────────────
// Ids here are UUIDs (companies, assets) AND plain numbers (ticket ids), plus
// the odd long hex token. All collapse to :id so /tickets/8652 and /tickets/9001
// aggregate as one screen — and no entity id is ever stored.
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,})$/i;

function cleanRoute() {
  try {
    const parts = window.location.pathname.split('/');
    const route = parts.map(p => (ID_SEGMENT.test(p) ? ':id' : p)).join('/') || '/';
    return route.toLowerCase().slice(0, 120);
  } catch { return '/'; }
}

// ── Element descriptor ──────────────────────────────────────────────────────
// tag + up to 3 STABLE classes. State classes (active, is-*, open…) are
// filtered so the same button doesn't split into five rows depending on its
// moment. No text, no ids, ever.
const STATE_CLASS = /^(active|is-|has-|open|closed|selected|show|hide|hover|focus|expanded|collapsed|disabled|loading|dragging|highlight|current)/;

function describe(el) {
  const tag = el.tagName ? String(el.tagName).toLowerCase() : 'unknown';
  const classes = [];
  // el.className is an SVGAnimatedString on svg nodes — only trust real strings.
  const raw = typeof el.className === 'string' ? el.className : '';
  for (const c of raw.split(/\s+/)) {
    if (!c || STATE_CLASS.test(c)) continue;
    if (!/^[a-z0-9_-]+$/i.test(c)) continue;
    classes.push(c.toLowerCase());
    if (classes.length >= 3) break;
  }
  return (tag + (classes.length ? '.' + classes.join('.') : '')).slice(0, 120);
}

// For a dead click the deepest target is often a bare <div> or an svg path
// inside an icon; climb a little to find something with a name, so the report
// reads "th.sortable" instead of "path".
function bestDescriptor(el) {
  let node = el;
  for (let i = 0; node && node.nodeType === 1 && i < 4; i++) {
    const d = describe(node);
    if (d.includes('.')) return d;
    node = node.parentElement;
  }
  return describe(el);
}

// ── Dead detection — the hard part ──────────────────────────────────────────
// "Alive" must err generous: a report of clicks-in-vain full of clicks that DO
// work gets read by nobody (medisoft week 1: ~670 false positives before these
// rules). Three layers:
//  1. Semantic interactives via closest(). NOTE: React attaches handlers at the
//     root, so [onclick] detects nothing here — roles and tags carry the load.
//  2. The cursor promise: any ancestor with pointer/grab cursor told the user
//     "you can click me". If the UI made the promise, the click was earned.
//  3. Modal/drawer backdrops (fixed, viewport-covering) — clicking them closes.
const INTERACTIVE = [
  'a', 'button', 'input', 'select', 'textarea', 'label', 'summary', 'option',
  '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="option"]',
  '[role="link"]', '[role="checkbox"]', '[role="switch"]', '[role="radio"]',
  '[contenteditable="true"]',
].join(', ');

const MAX_CURSOR_WALK = 8; // getComputedStyle is not free — bound the climb

function isAlive(el) {
  try {
    if (el.closest && el.closest(INTERACTIVE)) return true;
    let node = el;
    for (let depth = 0; node && node.nodeType === 1 && depth < MAX_CURSOR_WALK; depth++) {
      const st = window.getComputedStyle(node);
      const cur = st.cursor;
      if (cur === 'pointer' || cur === 'grab' || cur === 'grabbing') return true;
      if (st.position === 'fixed') {
        const r = node.getBoundingClientRect();
        if (r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9) {
          return true; // backdrop — tapping it closes the modal
        }
      }
      node = node.parentElement;
    }
    return false;
  } catch {
    // If detection itself breaks, calling a click dead would be a lie.
    return true;
  }
}

function onClick(e) {
  try {
    if (!e.isTrusted) return; // programmatic .click() is not a person
    const el = e.target;
    if (!el || el.nodeType !== 1) return;

    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const round4 = (n) => Math.round(Math.max(0, Math.min(1, n)) * 10000) / 10000;

    buffer.push({
      route: cleanRoute(),
      target: bestDescriptor(el),
      dead: !isAlive(el),
      xRel: vw > 0 ? round4(e.clientX / vw) : null,
      yRel: vh > 0 ? round4(e.clientY / vh) : null,
      viewportW: vw,
      viewportH: vh,
      sessionId: SESSION_ID, // same id as ui_actions — clicks and actions join
    });
    if (buffer.length >= MAX_BUFFER) { flush(); return; }
    if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
  } catch {
    // never let instrumentation throw into a click
  }
}

/** Install once. Safe to call from both Layout and PortalLayout — whichever
 *  mounts first wins; the guard makes the second call a no-op. */
export function installHeatmap() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}
